/**
 * A custom element that manages the account login actions.
 *
 * @extends {HTMLElement}
 */
class AccountLoginActions extends HTMLElement {
  /**
   * @type {Element | null}
   */
  shopLoginButton = null;

  connectedCallback() {
    this.shopLoginButton = this.querySelector('shop-login-button');

    if (this.shopLoginButton) {
      // We don't have control over the shop-login-button markup, so we need to set additional attributes here
      this.shopLoginButton.setAttribute('full-width', 'true');
      this.shopLoginButton.setAttribute('persist-after-sign-in', 'true');
      // Do this only if New Customer Account is ALWAYS the sign in option (and never Classic Customer Account)
      this.shopLoginButton.setAttribute('analytics-context', 'loginWithShopSelfServe');
      this.shopLoginButton.setAttribute('flow-version', 'account-actions-popover');
      
      // Ensure return-uri is properly encoded to avoid issues with special characters
      // Create a clean URL without error-related parameters that might interfere with authentication
      const currentUrl = new URL(window.location.href);
      // Remove any error-related query parameters that might interfere with the auth flow
      currentUrl.searchParams.delete('error');
      currentUrl.searchParams.delete('error_description');
      currentUrl.searchParams.delete('shopify_id');
      // Remove hash as it can interfere with authentication redirects
      currentUrl.hash = '';
      
      // Use the origin + pathname + cleaned search params as return-uri
      // This ensures we have a valid absolute URL without problematic query params or hash
      const returnUri = currentUrl.origin + currentUrl.pathname + (currentUrl.search ? currentUrl.search : '');
      this.shopLoginButton.setAttribute('return-uri', returnUri);

      // Reload the page after the login is completed, otherwise the page state is incorrect
      this.shopLoginButton.addEventListener('completed', () => {
        window.location.reload();
      });

      // Handle error events from shop-login-button (e.g., OTP verification failures)
      this.shopLoginButton.addEventListener('error', (event) => {
        console.error('Login error:', event.detail || event);
        // The error is already displayed by shop-login-button, but we can add additional handling here if needed
        this.#handleLoginError(event);
      });

      // Handle failed events (alternative error event name)
      this.shopLoginButton.addEventListener('failed', (event) => {
        console.error('Login failed:', event.detail || event);
        this.#handleLoginError(event);
      });

      // Handle any unhandled errors during the login flow
      this.shopLoginButton.addEventListener('login-error', (event) => {
        console.error('Login error event:', event.detail || event);
        this.#handleLoginError(event);
      });
    }

    // Check for error parameters in URL (in case of redirect back with error)
    this.#checkForUrlErrors();
  }

  /**
   * Handle login errors
   * @param {Event} event - The error event
   */
  #handleLoginError(event) {
    // Log detailed error information for debugging
    const errorDetails = {
      detail: event.detail,
      type: event.type,
      timestamp: new Date().toISOString(),
      url: window.location.href
    };
    console.error('Login error details:', errorDetails);
  }

  /**
   * Check URL for error parameters that might indicate authentication failures
   */
  #checkForUrlErrors() {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    const shopifyId = urlParams.get('shopify_id');

    if (error || errorDescription || shopifyId) {
      console.error('Authentication error detected in URL:', {
        error,
        errorDescription,
        shopifyId,
        url: window.location.href
      });
      
      // Clean up the URL by removing error parameters
      // This prevents the error from persisting if the user tries to log in again
      if (window.history && window.history.replaceState) {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('error');
        cleanUrl.searchParams.delete('error_description');
        cleanUrl.searchParams.delete('shopify_id');
        window.history.replaceState({}, '', cleanUrl.toString());
      }
      
      // If there's a significant error, try to reload after a short delay to reset state
      if (error && error !== 'access_denied') {
        setTimeout(() => {
          // Only reload if we're still on an error page
          if (window.location.search.includes('error=')) {
            window.location.href = window.location.pathname;
          }
        }, 1000);
      }
    }
  }
}

if (!customElements.get('account-login-actions')) {
  customElements.define('account-login-actions', AccountLoginActions);
}
